import { supabase } from "./supabase.js";
import { getDB } from "./schema.js";
import { setPersonFacets } from "../utils/filters.js";

// Follows sync (roadmap Batch 3, migrations/2026-10-15-follows.sql): followed
// people (every category, comic creators included) and comic series
// subscriptions, in one Supabase `follows` table keyed (kind, key).
//
// Follows are few and written from many places (follow buttons, the weekly
// "New from …" check, subscription checks), so instead of an outbox this is a
// snapshot reconcile: we remember a fingerprint of each record as last synced
// (per user, in localStorage), and on each sync
//   - a record that changed here since then is pushed (local wins a conflict),
//   - one that changed only in the cloud is applied here,
//   - one gone here but synced before becomes a cloud tombstone (`deleted`).
// Until the table exists every sync quietly fails and follows stay local.

export const FOLLOW_KINDS = [
	{ kind: "person", store: "followed_people", keyOf: (r) => r.person_key },
	{ kind: "subscription", store: "series_subscriptions", keyOf: (r) => String(r.series_id) },
];

export const FOLLOWS_CHANGED = "follows:changed";

// Writers call this; db/sync.js listens and syncs soon after.
export function followsChanged() {
	try {
		window.dispatchEvent(new Event(FOLLOWS_CHANGED));
	} catch {
		// not in a browser (tests)
	}
}

// Key-order-independent JSON (jsonb reorders keys), the fingerprint.
export function fingerprint(value) {
	if (value == null) return null;
	const norm = (v) => {
		if (Array.isArray(v)) return v.map(norm);
		if (v && typeof v === "object") {
			const out = {};
			for (const k of Object.keys(v).sort()) if (v[k] !== undefined) out[k] = norm(v[k]);
			return out;
		}
		return v;
	};
	return JSON.stringify(norm(value));
}

// Pure core. local/cloud: Map id → record (cloud: { data, deleted }); synced:
// object id → fingerprint at last sync. id = "<kind>:<key>".
// → { push: [{ id, data|null }], putLocal: [{ id, data }], deleteLocal: [id], synced }
export function reconcileFollows({ local, cloud, synced }) {
	const push = [];
	const putLocal = [];
	const deleteLocal = [];
	const next = {};
	const ids = new Set([...local.keys(), ...cloud.keys(), ...Object.keys(synced ?? {})]);
	for (const id of ids) {
		const L = local.get(id) ?? null;
		const C = cloud.get(id);
		const cloudData = C && !C.deleted ? C.data : null;
		const was = synced?.[id] ?? null;
		const hL = fingerprint(L);
		const hC = fingerprint(cloudData);
		const localChanged = hL !== was;
		const cloudChanged = hC !== was;
		if (localChanged && hL !== hC) {
			// Ours is newer (or the cloud never had it): push — a removal only if
			// the cloud still has a live copy.
			if (L || cloudData) push.push({ id, data: L });
			if (hL) next[id] = hL;
		} else if (cloudChanged && hL !== hC) {
			if (cloudData) putLocal.push({ id, data: cloudData });
			else deleteLocal.push(id);
			if (hC) next[id] = hC;
		} else if (hL) {
			next[id] = hL;
		}
	}
	return { push, putLocal, deleteLocal, synced: next };
}

const splitId = (id) => {
	const i = id.indexOf(":");
	return { kind: id.slice(0, i), key: id.slice(i + 1) };
};

export const followsSyncedKey = (uid) => `followsSynced:${uid}`;

function loadSynced(uid) {
	try {
		return JSON.parse(localStorage.getItem(followsSyncedKey(uid)) ?? "{}") ?? {};
	} catch {
		return {};
	}
}

function saveSynced(uid, synced) {
	try {
		localStorage.setItem(followsSyncedKey(uid), JSON.stringify(synced));
	} catch {
		// next sync re-merges (local wins, harmless)
	}
}

// Sign-out: the next sign-in merges from scratch (follows stay on the device).
export function resetFollowsSynced() {
	try {
		for (let i = localStorage.length - 1; i >= 0; i--) {
			const k = localStorage.key(i);
			if (k?.startsWith("followsSynced:")) localStorage.removeItem(k);
		}
	} catch {
		// nothing stored
	}
}

let warned = false;

// One full sync for `userId`. Returns true when local follows changed.
export async function syncFollows(userId) {
	if (!userId) return false;
	const { data: rows, error } = await supabase.from("follows").select("kind, key, data, deleted").eq("user_id", userId);
	if (error) {
		if (!warned) console.warn("Follows sync unavailable (run migrations/2026-10-15-follows.sql):", error.message ?? error);
		warned = true;
		return false;
	}
	warned = false;
	const db = await getDB();
	const local = new Map();
	for (const k of FOLLOW_KINDS) {
		for (const r of await db.getAll(k.store)) local.set(`${k.kind}:${k.keyOf(r)}`, r);
	}
	const cloud = new Map((rows ?? []).map((r) => [`${r.kind}:${r.key}`, { data: r.data, deleted: r.deleted }]));
	const plan = reconcileFollows({ local, cloud, synced: loadSynced(userId) });

	if (plan.push.length) {
		const upserts = plan.push.map(({ id, data }) => ({
			user_id: userId,
			...splitId(id),
			data: data ?? {},
			deleted: !data,
		}));
		const { error: pushErr } = await supabase.from("follows").upsert(upserts, { onConflict: "user_id,kind,key" });
		if (pushErr) {
			console.warn("Follows push failed (will retry):", pushErr.message ?? pushErr);
			return false;
		}
	}
	const storeOf = Object.fromEntries(FOLLOW_KINDS.map((k) => [k.kind, k.store]));
	for (const { id, data } of plan.putLocal) await db.put(storeOf[splitId(id).kind], data);
	for (const id of plan.deleteLocal) {
		const { kind, key } = splitId(id);
		await db.delete(storeOf[kind], kind === "subscription" ? Number(key) : key);
	}
	saveSynced(userId, plan.synced);
	const changed = plan.putLocal.length + plan.deleteLocal.length > 0;
	if (changed) setPersonFacets(await db.getAll("followed_people"));
	return changed;
}
